import { faClipboard } from "@fortawesome/pro-solid-svg-icons/faClipboard";
import { faPenToSquare } from "@fortawesome/pro-solid-svg-icons/faPenToSquare";
import { Flex, Text } from "@mantine/core";
import type { Dispatch, SetStateAction } from "react";

import { EDIT_COMPANY_MODAL_TEST_IDS } from "./edit-company-modal.constants";
import {
  AssessmentQuestionTable,
  BasicQuestionTable,
} from "@/components/screener-builder/review/tables";
import type {
  ReviewPageBasicQuestion,
  ReviewPageCompetency,
} from "@/types/screener-builder";
import type { Nullable } from "@/types/utils";

interface DefaultQuestionsSectionProps {
  basicQuestions: ReviewPageBasicQuestion[];
  disabled: boolean;
  openBasicQuestionEditor: () => void;
  openWrittenQuestionEditor: (
    competency: Nullable<ReviewPageCompetency>,
  ) => void;
  selectBasicQuestion: (question: ReviewPageBasicQuestion) => void;
  setBasicQuestions: Dispatch<SetStateAction<ReviewPageBasicQuestion[]>>;
  setWrittenQuestions: Dispatch<SetStateAction<ReviewPageCompetency[]>>;
  writtenQuestions: ReviewPageCompetency[];
}

export const DefaultQuestionsSection = ({
  basicQuestions,
  disabled,
  openBasicQuestionEditor,
  openWrittenQuestionEditor,
  selectBasicQuestion,
  setBasicQuestions,
  setWrittenQuestions,
  writtenQuestions,
}: DefaultQuestionsSectionProps) => (
  <>
    <div>
      <Flex align="center" gap={12} justify="space-between">
        <Text className="shrink-0 text-xs font-bold uppercase tracking-wide text-uw-gray-6">
          Default questions
        </Text>
        <Text c="gray.6" className="text-right text-xs font-medium">
          copied onto every new screener &middot; fully editable per screener
        </Text>
      </Flex>
      <div className="mt-2 h-px bg-uw-gray-1" />
    </div>

    <div data-testid={EDIT_COMPANY_MODAL_TEST_IDS.basicQuestionsTable}>
      <BasicQuestionTable
        addQuestionTestId={EDIT_COMPANY_MODAL_TEST_IDS.addBasicQuestionButton}
        allowDeleteLastQuestion
        basicQuestions={basicQuestions}
        emptyState={{
          description:
            "Add default basic questions to automatically include them in every new screener.",
          icon: faClipboard,
          title: "No default basic questions configured",
        }}
        isReadOnly={disabled}
        isUnframed
        onBasicQuestionReorder={(updater) =>
          setBasicQuestions((questions) => updater(questions))
        }
        onDeleteQuestion={(questionId, onSettled) => {
          setBasicQuestions((questions) =>
            questions.filter((question) => question.id !== questionId),
          );
          onSettled();
        }}
        openQuestionModal={openBasicQuestionEditor}
        setSelectedQuestion={selectBasicQuestion}
      />
    </div>

    <div data-testid={EDIT_COMPANY_MODAL_TEST_IDS.writtenQuestionsTable}>
      {/* TODO(AS-1925): Company-default AI generation is intentionally disabled
      for this PR. Re-enable the AI generation headerContent, radio group, and
      sample job context fields here when the follow-up resumes. */}
      <AssessmentQuestionTable
        addQuestionTestId={EDIT_COMPANY_MODAL_TEST_IDS.addWrittenQuestionButton}
        atMaxCompetencies={false}
        atMinimumAssessmentQuestions={false}
        competencies={writtenQuestions}
        emptyState={{
          description:
            "Add default assessment questions to automatically include them in every new screener.",
          icon: faPenToSquare,
          title: "No default written assessment questions configured",
        }}
        header="Written assessment questions"
        infoTooltipLabel="Questions that candidates answer by typing a text response"
        isLegacyScreener={false}
        isReadOnly={disabled}
        isUnframed
        onCompetencyReorder={(updater) =>
          setWrittenQuestions((competencies) => updater(competencies))
        }
        onDeleteCompetency={(competencyId, onSettled) => {
          setWrittenQuestions((competencies) =>
            competencies.filter((competency) => competency.id !== competencyId),
          );
          onSettled();
        }}
        openCompetencyModal={openWrittenQuestionEditor}
        setSelectedCompetency={openWrittenQuestionEditor}
      />
    </div>
  </>
);
